import AddCircleOutlineIcon from '@mui/icons-material/AddCircleOutline'
import CheckCircleIcon from '@mui/icons-material/CheckCircle'
import { Tooltip } from '@mui/material'
import { useEffect, useState } from 'react'
import { reduceGroupedOptions, withAsyncPaginate } from 'react-select-async-paginate'
import Creatable from 'react-select/creatable'
import {
  IcdCodeChip,
  formatIcdOptionLabel,
  getPrimarySpecializationId,
  getSuggestedDescriptorsForDiagnosis,
  icdGroupStyles,
  makeClinicalEntityLoadOptions,
} from './icdSelectSupport'
// commonStyles lives with the report-making selects; that module imports only
// icdSelectSupport (never this file), so there is no import cycle
import { commonStyles } from 'src/mentor/components/TherapyReportMakingPage/TherapyReportMakingPageComponents'

const CreatableAsyncPaginate = withAsyncPaginate(Creatable)

const loadOptionsSymptoms = makeClinicalEntityLoadOptions('clinical-symptom')

/**
 * ICD-aware clinical-symptom multi-select (WHO R-codes + the curated symptom vocabulary,
 * both served IDB-first). Same affordances as ClinicalDiagnosisSelectMenu: code chips,
 * WHO detail tooltips, block grouping, discipline scoping via doctorSpecializations.
 */
export const ClinicalSymptomSelectMenu = ({
  clinicId,
  selectedSymptoms,
  setSelectedSymptoms,
  isMulti = true,
  doctorSpecializations = null,
  placeholder = '',
}) => {
  const specializationId = getPrimarySpecializationId(doctorSpecializations)

  return (
    <div style={{ width: '100%', position: 'relative' }}>
      <CreatableAsyncPaginate
        id="clinicalSymptomSelection"
        placeholder={placeholder}
        isMulti={isMulti}
        isClearable={true}
        isSearchable={true}
        defaultOptions={true}
        loadOptions={loadOptionsSymptoms}
        getOptionValue={(option) => option?.value}
        getOptionLabel={(option) => option?.label}
        formatOptionLabel={formatIcdOptionLabel}
        value={selectedSymptoms}
        onChange={(value) => setSelectedSymptoms(value || null)}
        debounceTimeout={500}
        additional={{ page: 1, clinicId, specializationId }}
        cacheUniqs={[clinicId, specializationId]}
        styles={{ ...commonStyles, ...icdGroupStyles }}
        menuPortalTarget={document.body}
        menuPosition="fixed"
        shouldLoadMore={(scrollHeight, clientHeight, scrollTop) => {
          const threshold = 50
          return scrollHeight - clientHeight - scrollTop < threshold
        }}
        reduceOptions={reduceGroupedOptions}
      />
    </div>
  )
}

// UI-only props added by react-select; stripped before the entity is embedded in the report
const stripSelectProps = ({ label, value, __isNew__, ...entity }) => entity

/**
 * Enrichment panel for a selected clinical diagnosis: attach associated symptoms and toggle
 * suggested clinical descriptors (risk flags, screening prompts, monitoring indicators, …
 * suggested by matching the descriptor's relatedDiagnoses ICD codes to the diagnosis).
 * Everything is written INTO the diagnosis object (symptoms / clinicalDescriptors +
 * their id/code mirrors), which the report embeds wholesale — no new report fields needed.
 */
export const ClinicalDiagnosisEnrichment = ({
  diagnosis,
  onDiagnosisChange,
  clinicId,
  doctorSpecializations = null,
}) => {
  const [suggestedDescriptors, setSuggestedDescriptors] = useState([])
  const icd10Code = diagnosis?.icd10Code || null

  useEffect(() => {
    let cancelled = false
    if (!icd10Code) {
      setSuggestedDescriptors([])
      return undefined
    }
    getSuggestedDescriptorsForDiagnosis(
      diagnosis,
      getPrimarySpecializationId(doctorSpecializations),
    ).then((descriptors) => {
      if (!cancelled) setSuggestedDescriptors(descriptors)
    })
    return () => {
      cancelled = true
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [icd10Code])

  if (!diagnosis) {
    return null
  }

  const attachedDescriptors = diagnosis?.clinicalDescriptors || []
  const attachedSymptoms = diagnosis?.symptoms || []
  const isAttached = (descriptor) =>
    attachedDescriptors.some((item) => item?.id === descriptor?.id)

  const setSymptoms = (symptoms) => {
    const plainSymptoms = (symptoms || []).map(stripSelectProps)
    onDiagnosisChange({
      ...diagnosis,
      symptoms: plainSymptoms,
      symptomIds: plainSymptoms.map((symptom) => symptom?.id).filter(Boolean),
      symptomCodes: plainSymptoms.map((symptom) => symptom?.code).filter(Boolean),
    })
  }

  const toggleDescriptor = (descriptor) => {
    const next = isAttached(descriptor)
      ? attachedDescriptors.filter((item) => item?.id !== descriptor?.id)
      : [...attachedDescriptors, stripSelectProps(descriptor)]
    onDiagnosisChange({
      ...diagnosis,
      clinicalDescriptors: next,
      clinicalDescriptorIds: next.map((item) => item?.id).filter(Boolean),
    })
  }

  const descriptorsToRender = [
    ...attachedDescriptors,
    ...suggestedDescriptors.filter((descriptor) => !isAttached(descriptor)),
  ]

  return (
    <div style={{ display: 'flex', flexDirection: 'column', gap: '8px', marginTop: '8px' }}>
      <div>
        <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b21a8', marginBottom: '4px' }}>
          Associated symptoms
        </div>
        <ClinicalSymptomSelectMenu
          clinicId={clinicId}
          doctorSpecializations={doctorSpecializations}
          selectedSymptoms={attachedSymptoms.map((symptom) => ({
            ...symptom,
            value: symptom?.id || symptom?.title,
            label: symptom?.title,
          }))}
          setSelectedSymptoms={setSymptoms}
          placeholder="Search WHO R-codes & symptom vocabulary…"
        />
      </div>

      {descriptorsToRender.length > 0 && (
        <div>
          <div style={{ fontSize: '12px', fontWeight: 700, color: '#6b21a8', marginBottom: '4px' }}>
            Clinical descriptors{' '}
            <span style={{ fontWeight: 400, opacity: 0.7 }}>
              (suggested for {icd10Code} — click to attach)
            </span>
          </div>
          <div style={{ display: 'flex', flexWrap: 'wrap', gap: '6px' }}>
            {descriptorsToRender.map((descriptor) => {
              const attached = isAttached(descriptor)
              return (
                <Tooltip
                  key={descriptor?.id || descriptor?.title}
                  title={descriptor?.description || ''}
                  placement="top"
                >
                  <span
                    onClick={() => toggleDescriptor(descriptor)}
                    style={{
                      display: 'inline-flex',
                      alignItems: 'center',
                      gap: '4px',
                      fontSize: '12px',
                      lineHeight: '18px',
                      padding: '3px 9px',
                      borderRadius: '12px',
                      cursor: 'pointer',
                      userSelect: 'none',
                      border: attached
                        ? '1px solid rgba(124, 58, 237, 0.9)'
                        : '1px dashed rgba(124, 58, 237, 0.45)',
                      backgroundColor: attached ? 'rgba(124, 58, 237, 0.12)' : '#ffffff',
                      color: '#5b21b6',
                    }}
                  >
                    {attached ? (
                      <CheckCircleIcon sx={{ fontSize: 14 }} />
                    ) : (
                      <AddCircleOutlineIcon sx={{ fontSize: 14, opacity: 0.6 }} />
                    )}
                    <span>{descriptor?.title}</span>
                    {descriptor?.descriptorType && (
                      <IcdCodeChip
                        code={descriptor.descriptorType}
                        style={{ color: '#6b21a8', textTransform: 'none', fontWeight: 600 }}
                      />
                    )}
                  </span>
                </Tooltip>
              )
            })}
          </div>
        </div>
      )}
    </div>
  )
}
